import path from 'path';

import { CursorPagedResults } from '~interfaces';
import {
	BaseReferenceResponse,
	IdReferenceResponse,
	SkcCursorPagedResponse,
	SkcSingleResponse,
} from '~interfaces/responses';
// import TopologyProxiedSkopeiConnectService from '~services/topologyProxiedSkopeiConnectService';
import { TopologyProxiedSkopeiConnectService } from '~services';

import AccessActionTypeEnum from '../enums/accessActionTypeEnum';
import AccessEventEnum from '../enums/accessEventEnum';
import AccessLog from '../interfaces/accessLog';

/**
 * A service that does calls to the Skopei Connect API through
 * the Topology backend
 */
class SkcAccessLogsService extends TopologyProxiedSkopeiConnectService {
	public readonly path = 'logs/access';

	async getAccessLogs({
		pageSize = 10,
		...args
	}: {
		cursor?: string;
		pageSize: number;
		organisationId: string;
		searchQuery?: string;
		after?: Date;
		before?: Date;
		deviceId?: string;
		keyId?: string;
	}): Promise<CursorPagedResults<AccessLog>> {
		const { data } = await this._client.get<SkcCursorPagedResponse<SkcAccessLogResponse>>(
			this.path,
			{
				params: {
					cursor: args.cursor,
					'page-size': pageSize,
					'organisation-id': args.organisationId,
					'search-query': args.searchQuery || undefined,
					'timestamp-before': args.before,
					'timestamp-after': args.after,
					'device-id': args.deviceId,
					'key-id': args.keyId,
				},
			}
		);

		return this.mapCursorPagedResponse(data, SkcAccessLogsService.fromResponse);
	}

	async getAccessLogById(id: string): Promise<AccessLog> {
		const { data } = await this._client.get<SkcSingleResponse<SkcAccessLogResponse>>(
			path.join(this.path, id)
		);

		return SkcAccessLogsService.fromResponse(data.data);
	}

	static fromResponse(data: SkcAccessLogResponse): AccessLog {
		const {
			id,
			event,
			timestamp,
			rule,
			key,
			device,
			organisation,
			correlationId,
			origin,
			accessType,
			...rest
		} = data;

		return {
			...rest,
			id: id.toString(),
			timestamp: new Date(timestamp),
			eventType: skcAccessEventMapper[event] ?? event,
			rule: SkcAccessLogsService.fromBaseReferenceResponse(rule),
			key: SkcAccessLogsService.fromBaseReferenceResponse(key),
			device: SkcAccessLogsService.fromBaseReferenceResponse(device),
			organisation: SkcAccessLogsService.fromBaseReferenceResponse(organisation),
			correlationId: correlationId > 0 ? correlationId?.toString() : undefined,
			accessType: accessType as AccessActionTypeEnum,
			origin:
				origin === 'deviceOrigin' ? 'device'
				: origin === 'serverOrigin' ? 'server'
				: origin,
		};
	}
}

// Add key value pairs that need mapping. We should fallback to the event in the response
const skcAccessEventMapper: Partial<Record<SkcAccessEventTypeResponse, AccessEventEnum | string>> =
	{
		accessGranted: AccessEventEnum.GrantedRule,
		accessDeniedKeyNotFound: AccessEventEnum.CardNotFound,
		// 'accessDeniedNoRuleFound': AccessEventEnum.NoRuleFound,
		// 'accessDeniedKeyHashMissMatch': AccessEventEnum.HashMissMatch
	};

type SkcAccessEventTypeResponse =
	| 'accessGranted'
	| 'accessOfficeModeGranted'
	| 'accessOfficeModeExit'
	| 'accessDenied'
	| 'accessDeniedKeyNotFound'
	| 'accessDeniedNoRuleFound'
	| 'accessDeniedNoConnection'
	| 'accessDeniedInvalidRequest'
	| 'accessDeniedKeyHashMissMatch'
	| 'error';

interface SkcAccessLogResponse extends IdReferenceResponse {
	event: SkcAccessEventTypeResponse;
	timestamp: string;
	rule?: BaseReferenceResponse;
	key?: BaseReferenceResponse;
	device?: BaseReferenceResponse;
	organisation: BaseReferenceResponse;
	correlationId: number;
	origin: 'server' | 'serverOrigin' | 'device' | 'deviceOrigin';
	accessType:
		| 'rfid'
		| 'ble'
		| 'server'
		| 'serverScheduled'
		| 'serverTriggered'
		| 'memory'
		| 'unspecified';
	deviceVerified: boolean;
}

export default SkcAccessLogsService;
